import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Plus, Search, Eye, Calendar, DollarSign, User, Package, Printer, MapPin, Truck, Monitor, Warehouse, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { GoogleMapsLoader } from '../../components/maps/GoogleMapsLoader';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import NewSaleForm from './NewSaleFormRefactored';
import PendingOrdersBar from './PendingOrdersBar';
import apiService from '../../services/apiService';
import { formatMoney, formatDate } from '../../utils/formatters';
import './SalesPage.css';

// Constante para localStorage
const PENDING_ORDERS_KEY = 'pos_pedidos_pendientes';

// Función auxiliar para obtener la fecha de hoy en formato YYYY-MM-DD
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SalesPageContent = () => {
  const { loading, setLoading, showToast } = useApp();

  const [ventas, setVentas] = useState([]);
  const [filteredVentas, setFilteredVentas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVenta, setSelectedVenta] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNewSaleModal, setShowNewSaleModal] = useState(false);
  const [selectedVentas, setSelectedVentas] = useState([]); // IDs de ventas seleccionadas
  const [filtroTipoEntrega, setFiltroTipoEntrega] = useState('todos'); // 'todos', 'domicilio', 'pickup'
  const [filtroFecha, setFiltroFecha] = useState('hoy'); // '', 'hoy', 'rango'
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Estado para pedidos pendientes (en espera)
  const [pedidosPendientes, setPedidosPendientes] = useState([]);
  const [pedidoActivo, setPedidoActivo] = useState(null); // Pedido que se está editando

  // Estado para configuraciones de impresión
  const [configImpresion, setConfigImpresion] = useState({
    impresion_preguntar_ubicacion: true,
    impresion_ubicacion_default: 'local',
    impresion_copias_ticket: 1
  });
  const [showPrintLocationModal, setShowPrintLocationModal] = useState(false);
  const [ventaParaImprimir, setVentaParaImprimir] = useState(null);

  // Estado para configuraciones de formato de impresión
  const [empresaConfig, setEmpresaConfig] = useState({
    logo: '', nombre: '', formatoColor: '#1e40af', formatoMostrarLogo: true,
    formatoInfoEmpresa: '', formatoMarcaAgua: true, formatoMarcaAguaOpacidad: 5,
    formatoMarcaAguaTamano: 500, formatoMostrarFirma: false, formatoDesgloseIva: false,
    formatoPiePagina: '', formatoCopiaOriginal: true, formatoCopiaCliente: false, formatoCopiaMostrador: false
  });

  // Cargar configuraciones de impresión y empresa
  const cargarConfiguracionesImpresion = useCallback(async () => {
    try {
      const nombrePc = localStorage.getItem('pos_nombre_pc') || null;
      const [impResult, empResult] = await Promise.all([
        apiService.getConfiguracionesPorCategoria('impresion', nombrePc),
        apiService.getConfiguracionesPorCategoria('empresa', null)
      ]);
      if (impResult.success && impResult.data.valores) {
        setConfigImpresion(prev => ({ ...prev, ...impResult.data.valores }));
      }
      if (empResult.success && empResult.data.valores) {
        const v = empResult.data.valores;
        setEmpresaConfig({
          logo: v.empresa_logo || '',
          nombre: v.empresa_nombre || '',
          formatoColor: v.empresa_formato_color || '#1e40af',
          formatoMostrarLogo: v.empresa_formato_mostrar_logo !== false && v.empresa_formato_mostrar_logo !== 'false',
          formatoInfoEmpresa: v.empresa_formato_info_empresa || '',
          formatoMarcaAgua: v.empresa_formato_marca_agua !== false && v.empresa_formato_marca_agua !== 'false',
          formatoMarcaAguaOpacidad: parseFloat(v.empresa_formato_marca_agua_opacidad) || 5,
          formatoMostrarFirma: v.empresa_formato_mostrar_firma === true || v.empresa_formato_mostrar_firma === 'true',
          formatoDesgloseIva: v.empresa_formato_desglose_iva === true || v.empresa_formato_desglose_iva === 'true',
          formatoPiePagina: v.empresa_formato_pie_pagina || '',
          formatoMarcaAguaTamano: parseFloat(v.empresa_formato_marca_agua_tamano) || 500,
          formatoCopiaOriginal: v.empresa_formato_copia_original !== false && v.empresa_formato_copia_original !== 'false',
          formatoCopiaCliente: v.empresa_formato_copia_cliente === true || v.empresa_formato_copia_cliente === 'true',
          formatoCopiaMostrador: v.empresa_formato_copia_mostrador === true || v.empresa_formato_copia_mostrador === 'true',
          formatoLogoTamano: parseInt(v.empresa_formato_logo_tamano) || 100,
          formatoInfoTamano: parseInt(v.empresa_formato_info_tamano) || 9,
          formatoClienteTamano: parseInt(v.empresa_formato_cliente_tamano) || 10,
          formatoPagare: v.empresa_formato_pagare === true || v.empresa_formato_pagare === 'true',
          formatoPagareTexto: v.empresa_formato_pagare_texto || ''
        });
      }
    } catch (error) {
      console.error('Error cargando configuraciones de impresión:', error);
    }
  }, []);

  useEffect(() => {
    loadVentas();
    cargarConfiguracionesImpresion();
    cargarPedidosPendientes();
  }, [cargarConfiguracionesImpresion]);

  // Cargar pedidos pendientes desde localStorage
  const cargarPedidosPendientes = () => {
    try {
      const stored = localStorage.getItem(PENDING_ORDERS_KEY);
      if (stored) {
        const pedidos = JSON.parse(stored);
        // Filtrar pedidos muy antiguos (más de 24 horas)
        const ahora = Date.now();
        const pedidosValidos = pedidos.filter(p => (ahora - p.timestamp) < 24 * 60 * 60 * 1000);
        setPedidosPendientes(pedidosValidos);
        // Actualizar localStorage si se eliminaron algunos
        if (pedidosValidos.length !== pedidos.length) {
          localStorage.setItem(PENDING_ORDERS_KEY, JSON.stringify(pedidosValidos));
        }
      }
    } catch (error) {
      console.error('Error cargando pedidos pendientes:', error);
    }
  };

  // Retomar un pedido pendiente
  const handleRetomarPedido = (pedidoId) => {
    const pedido = pedidosPendientes.find(p => p.id === pedidoId);
    if (pedido) {
      setPedidoActivo(pedido);
      setShowNewSaleModal(true);
    }
  };

  // Eliminar un pedido pendiente
  const handleEliminarPedidoPendiente = (pedidoId) => {
    if (window.confirm('¿Eliminar este pedido en espera?')) {
      const nuevosPedidos = pedidosPendientes.filter(p => p.id !== pedidoId);
      setPedidosPendientes(nuevosPedidos);
      localStorage.setItem(PENDING_ORDERS_KEY, JSON.stringify(nuevosPedidos));
      showToast('Pedido eliminado', 'info');
    }
  };

  // Callback cuando se pausa un pedido desde NewSaleForm
  const handlePedidoPausado = (pedidoData) => {
    const nuevosPedidos = [...pedidosPendientes];
    const existingIndex = nuevosPedidos.findIndex(p => p.id === pedidoData.id);

    if (existingIndex >= 0) {
      // Actualizar pedido existente
      nuevosPedidos[existingIndex] = pedidoData;
    } else {
      // Agregar nuevo pedido
      nuevosPedidos.push(pedidoData);
    }

    setPedidosPendientes(nuevosPedidos);
    localStorage.setItem(PENDING_ORDERS_KEY, JSON.stringify(nuevosPedidos));
    showToast('Pedido guardado en espera', 'success');
  };

  // Callback cuando se completa una venta (eliminar de pendientes si existía)
  const handleVentaCompletada = (pedidoId) => {
    if (pedidoId) {
      const nuevosPedidos = pedidosPendientes.filter(p => p.id !== pedidoId);
      setPedidosPendientes(nuevosPedidos);
      localStorage.setItem(PENDING_ORDERS_KEY, JSON.stringify(nuevosPedidos));
    }
    setPedidoActivo(null);
    loadVentas();
  };

  useEffect(() => {
    filterVentas();
  }, [ventas, searchQuery, filtroTipoEntrega, filtroFecha, fechaInicio, fechaFin]);

  // Listener de teclado para F3
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F3 para nueva venta
      if (e.key === 'F3') {
        e.preventDefault();
        setShowNewSaleModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadVentas = async () => {
    try {
      setLoading(true);
      const result = await apiService.getVentas();

      if (result.success) {
        setVentas(result.data);
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      showToast('Error al cargar ventas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterVentas = () => {
    let filtered = ventas;

    // Filtrar por búsqueda de texto
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(venta => {
        return (
          venta.id?.toString().includes(query) ||
          venta.folio?.toLowerCase().includes(query) ||
          venta.cliente_nombre?.toLowerCase().includes(query) ||
          venta.total?.toString().includes(query)
        );
      });
    }

    // Filtrar por tipo de entrega
    if (filtroTipoEntrega !== 'todos') {
      filtered = filtered.filter(venta => {
        if (filtroTipoEntrega === 'domicilio') {
          return venta.tipo_entrega === 'domicilio';
        } else if (filtroTipoEntrega === 'pickup') {
          return venta.tipo_entrega === 'pickup' || !venta.tipo_entrega;
        }
        return true;
      });
    }

    // Filtrar por fecha
    if (filtroFecha === 'hoy') {
      const hoy = getTodayDate(); // Uses the getTodayDate() function which returns YYYY-MM-DD
      filtered = filtered.filter(venta => {
        // Extract just the date part from the timestamp string directly
        const fechaVenta = venta.fecha_creacion ? venta.fecha_creacion.split(' ')[0] : '';
        return fechaVenta === hoy;
      });
    } else if (filtroFecha === 'rango' && fechaInicio && fechaFin) {
      filtered = filtered.filter(venta => {
        // Extract just the date part from the timestamp string directly
        const fechaVenta = venta.fecha_creacion ? venta.fecha_creacion.split(' ')[0] : '';
        return fechaVenta >= fechaInicio && fechaVenta <= fechaFin;
      });
    }

    setFilteredVentas(filtered);
  };

  // Funciones de selección
  const handleSelectVenta = (ventaId) => {
    setSelectedVentas(prev => {
      if (prev.includes(ventaId)) {
        // Si ya está seleccionada, la quitamos
        return prev.filter(id => id !== ventaId);
      } else {
        // Si no está seleccionada, la agregamos
        return [...prev, ventaId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedVentas.length === filteredVentas.length) {
      // Si todas están seleccionadas, deseleccionar todas
      setSelectedVentas([]);
    } else {
      // Seleccionar todas las ventas filtradas
      setSelectedVentas(filteredVentas.map(venta => venta.id));
    }
  };

  const isVentaSelected = (ventaId) => {
    return selectedVentas.includes(ventaId);
  };

  const areAllSelected = () => {
    return filteredVentas.length > 0 && selectedVentas.length === filteredVentas.length;
  };

  const areSomeSelected = () => {
    return selectedVentas.length > 0 && selectedVentas.length < filteredVentas.length;
  };

  const handleViewDetail = async (venta) => {
    try {
      setLoading(true);

      // Obtener detalle completo de la venta
      const result = await apiService.getVenta(venta.id);

      if (result.success) {
        setSelectedVenta(result.data);
        setShowDetailModal(true);
      } else {
        showToast('Error al cargar detalle de venta', 'error');
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      showToast('Error al cargar detalle de venta', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReimprimirVenta = async (venta) => {
    // Verificar si debemos preguntar la ubicación de impresión
    if (configImpresion.impresion_preguntar_ubicacion) {
      setVentaParaImprimir(venta);
      setShowPrintLocationModal(true);
      return;
    }

    // Si no hay que preguntar, imprimir directamente en la ubicación default
    await ejecutarImpresion(venta, configImpresion.impresion_ubicacion_default);
  };

  // Ejecutar la impresión en la ubicación especificada
  const ejecutarImpresion = async (venta, ubicacion) => {
    try {
      setLoading(true);

      // Obtener el ticket desde el API
      const ticketResult = await apiService.imprimirTicket(venta.id);

      if (ticketResult.success) {
        await imprimirTicket(ticketResult.data.ticket, ticketResult.data.folio, ubicacion);
        showToast(`Ticket enviado a impresora (${ubicacion === 'local' ? 'Local' : 'Almacén'})`, 'success');
      } else {
        showToast('Error al generar ticket', 'error');
      }
    } catch (error) {
      console.error('Error al reimprimir ticket:', error);
      showToast('Error al reimprimir ticket', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de ubicación de impresión
  const handleSelectPrintLocation = async (ubicacion) => {
    setShowPrintLocationModal(false);
    if (ventaParaImprimir) {
      await ejecutarImpresion(ventaParaImprimir, ubicacion);
      setVentaParaImprimir(null);
    }
  };

  // Función para imprimir ticket usando Electron IPC
  const imprimirTicket = async (ticketTexto, folio, ubicacion = 'local') => {
    try {
      // Verificar si estamos en Electron
      if (!window.electron || !window.electron.ipcRenderer) {
        console.warn('Electron IPC no disponible, usando impresión del navegador');
        imprimirTicketNavegador(ticketTexto, folio);
        return;
      }

      // Obtener impresora según ubicación
      let printerName;
      if (ubicacion === 'almacen') {
        printerName = localStorage.getItem('ticket_printer_almacen');
      } else {
        printerName = localStorage.getItem('ticket_printer');
      }

      if (!printerName) {
        // Si no hay impresora guardada, obtener la predeterminada del sistema
        try {
          const result = await window.electron.ipcRenderer.invoke('get-default-printer');
          if (result.success && result.printer) {
            printerName = result.printer.name;
            console.log('Usando impresora predeterminada del sistema:', printerName);
          } else {
            printerName = 'Microsoft Print to PDF'; // Fallback
            console.warn('No se pudo obtener impresora predeterminada, usando fallback');
          }
        } catch (error) {
          console.error('Error al obtener impresora predeterminada:', error);
          printerName = 'Microsoft Print to PDF'; // Fallback
        }
      }

      // Convertir texto a HTML
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Ticket - ${folio}</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              font-weight: bold;
              color: #000;
              margin: 0;
              padding: 5px;
              white-space: pre-wrap;
              background: white;
              line-height: 1.2;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @media print {
              @page { margin: 0; size: 80mm auto; }
              body {
                margin: 0;
                padding: 2px;
                font-weight: bold;
                color: #000;
                max-width: 80mm;
              }
            }
          </style>
        </head>
        <body>${ticketTexto}</body>
        </html>
      `;

      console.log('Imprimiendo ticket en:', printerName);

      // Enviar a impresora usando Electron IPC
      const result = await window.electron.ipcRenderer.invoke('print-document', {
        printerName: printerName,
        htmlContent: htmlContent,
        silent: true,
        options: {
          margins: { marginType: 'none' },
          pageSize: 'A4'
        }
      });

      if (result.success) {
        console.log('Ticket impreso correctamente');
      } else {
        console.error('Error al imprimir ticket:', result.message);
        showToast('Error al imprimir ticket: ' + result.message, 'warning');
      }

    } catch (error) {
      console.error('Error en impresión de ticket:', error);
      showToast('Error al imprimir ticket', 'error');
    }
  };

  // Función de respaldo para imprimir en el navegador
  const imprimirTicketNavegador = (ticketTexto, folio) => {
    const ventanaImpresion = window.open('', 'Imprimir Ticket', 'width=400,height=600');

    if (ventanaImpresion) {
      ventanaImpresion.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ticket - ${folio}</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              font-weight: bold;
              color: #000;
              margin: 0;
              padding: 10px;
              white-space: pre-wrap;
              background: white;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @media print {
              body {
                margin: 0;
                padding: 5px;
                font-weight: bold;
                color: #000;
                max-width: 80mm;
              }
            }
          </style>
        </head>
        <body>${ticketTexto}</body>
        </html>
      `);

      ventanaImpresion.document.close();

      ventanaImpresion.onload = function () {
        ventanaImpresion.focus();
        setTimeout(() => {
          ventanaImpresion.print();
        }, 250);
      };
    }
  };

  // Función para imprimir pedidos seleccionados en formato carta
  const handleImprimirPedidosSeleccionados = async () => {
    try {
      setLoading(true);

      // Obtener los datos completos de las ventas seleccionadas
      const ventasCompletas = [];
      for (const ventaId of selectedVentas) {
        const result = await apiService.getVenta(ventaId);
        if (result.success) {
          ventasCompletas.push(result.data);
        }
      }

      if (ventasCompletas.length === 0) {
        showToast('No se pudieron cargar los pedidos', 'error');
        return;
      }

      // Generar HTML para impresión en carta
      const htmlContent = generarHTMLPedidos(ventasCompletas);

      // Verificar si estamos en Electron
      if (window.electron && window.electron.ipcRenderer) {
        // Obtener impresora
        let printerName = configImpresion.impresion_impresora_predeterminada || '';
        if (!printerName) {
          try {
            const printerResult = await window.electron.ipcRenderer.invoke('get-default-printer');
            if (printerResult.success && printerResult.printer) printerName = printerResult.printer.name;
          } catch (e) { /* fallback */ }
        }
        if (!printerName) printerName = 'Microsoft Print to PDF';

        const result = await window.electron.ipcRenderer.invoke('print-document', {
          printerName,
          htmlContent: htmlContent,
          silent: false,
          options: {
            margins: { marginType: 'default' },
            pageSize: 'Letter',
            landscape: false
          }
        });

        if (result.success) {
          showToast('Pedidos enviados a impresora', 'success');
        } else {
          showToast('Error al imprimir: ' + result.message, 'error');
        }
      } else {
        // Fallback a impresión del navegador
        imprimirPedidosNavegador(htmlContent);
      }

    } catch (error) {
      console.error('Error al imprimir pedidos:', error);
      showToast('Error al imprimir pedidos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para generar HTML de pedidos en formato carta
  const generarHTMLPedidos = (ventas) => {
    const pedidosHTML = ventas.map(venta => {
      return `
        <div class="pedido">
          <div class="pedido-header">
            <h2>Pedido: ${venta.folio || '#' + venta.id}</h2>
            <div class="pedido-fecha">${formatDate(venta.fecha_creacion)}</div>
          </div>
          <div class="pedido-info">
            <div class="info-item">
              <strong>Cliente:</strong>
              <span>${venta.cliente_nombre || 'Cliente General'}</span>
            </div>
            ${venta.tipo_entrega === 'domicilio' && venta.direccion_entrega ? `
              <div class="info-item">
                <strong>Dirección de Entrega:</strong>
                <span>${venta.direccion_entrega}</span>
              </div>
            ` : `
              <div class="info-item">
                <strong>Tipo de Entrega:</strong>
                <span>Pasan por Él (Pickup)</span>
              </div>
            `}
          </div>
        </div>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Pedidos de Entrega</title>
        <style>
          @page {
            size: letter;
            margin: 1.5cm;
          }

          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #000;
            background: white;
          }

          h1 {
            text-align: center;
            font-size: 24px;
            margin-bottom: 30px;
            color: #333;
            border-bottom: 3px solid #667eea;
            padding-bottom: 10px;
          }

          .pedido {
            page-break-inside: avoid;
            margin-bottom: 25px;
            padding: 20px;
            border: 2px solid #667eea;
            border-radius: 8px;
            background: #f8f9fa;
          }

          .pedido-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #667eea;
          }

          .pedido-header h2 {
            margin: 0;
            font-size: 20px;
            color: #667eea;
          }

          .pedido-fecha {
            font-size: 14px;
            color: #666;
          }

          .pedido-info {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .info-item {
            display: flex;
            gap: 10px;
            font-size: 16px;
            line-height: 1.6;
          }

          .info-item strong {
            min-width: 150px;
            color: #333;
          }

          .info-item span {
            flex: 1;
            color: #000;
            font-weight: 500;
          }

          @media print {
            body {
              padding: 0;
            }

            .pedido {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <h1>Pedidos de Entrega</h1>
        ${pedidosHTML}
      </body>
      </html>
    `;
  };

  // Función de respaldo para imprimir pedidos en el navegador
  const imprimirPedidosNavegador = (htmlContent) => {
    const ventanaImpresion = window.open('', 'Imprimir Pedidos', 'width=800,height=600');

    if (ventanaImpresion) {
      ventanaImpresion.document.write(htmlContent);
      ventanaImpresion.document.close();

      ventanaImpresion.onload = function () {
        ventanaImpresion.focus();
        setTimeout(() => {
          ventanaImpresion.print();
        }, 250);
      };
    }
  };

  // Abreviar unidades de medida para formatos de impresión
  const abreviarUnidad = (unidad) => {
    if (!unidad) return 'PZA';
    const map = {
      'pieza': 'PZA', 'piezas': 'PZA', 'pz': 'PZA', 'pza': 'PZA',
      'kilogramo': 'KG', 'kilogramos': 'KG', 'kilo': 'KG', 'kilos': 'KG', 'kg': 'KG',
      'gramo': 'GR', 'gramos': 'GR', 'g': 'GR', 'gr': 'GR',
      'caja': 'CJA', 'cajas': 'CJA', 'cja': 'CJA',
      'paquete': 'PQT', 'paquetes': 'PQT', 'pqt': 'PQT',
      'bulto': 'BLT', 'bultos': 'BLT', 'blt': 'BLT',
      'millar': 'MIL', 'millares': 'MIL', 'mil': 'MIL',
      'litro': 'LT', 'litros': 'LT', 'lt': 'LT', 'l': 'LT',
      'metro': 'MT', 'metros': 'MT', 'mt': 'MT', 'm': 'MT',
      'rollo': 'RLL', 'rollos': 'RLL', 'rll': 'RLL',
      'bolsa': 'BLS', 'bolsas': 'BLS', 'bls': 'BLS',
      'costal': 'CST', 'costales': 'CST', 'cst': 'CST',
    };
    return map[unidad.toLowerCase().trim()] || unidad.toUpperCase().substring(0, 3);
  };

  // Generar HTML formato carta para reimpresión
  const generarFormatoPedidoHTML = (venta) => {
    const fecha = venta.fecha_creacion ? formatDate(venta.fecha_creacion) : new Date().toLocaleString('es-MX');
    const color = empresaConfig.formatoColor;
    const logo = empresaConfig.logo;
    const opacidad = empresaConfig.formatoMarcaAguaOpacidad / 100;
    const tamanoMarcaAgua = empresaConfig.formatoMarcaAguaTamano;
    const total = venta.total || (venta.detalles || []).reduce((s, d) => s + parseFloat(d.subtotal || 0), 0);
    const mostrarFirma = empresaConfig.formatoMostrarFirma || venta.metodo_pago === 'credito';
    const logoTamano = empresaConfig.formatoLogoTamano || 100;
    const infoFontSize = empresaConfig.formatoInfoTamano || 9;
    const clienteFontSize = empresaConfig.formatoClienteTamano || 10;
    const mostrarPagare = empresaConfig.formatoPagare && venta.estatus_pago !== 'pagado';
    const pagareTextoDefault = 'POR EL PRESENTE PAGARÉ RECONOZCO(EMOS) DEBER Y ME(NOS) OBLIGO(AMOS) INCONDICIONAL A PAGAR A LA ORDEN DE {empresa} LA CANTIDAD QUE SE ORIGINA EN SATISFACCIÓN CAUSARÁ INTERESES MORATORIOS DE 6% MENSUAL. DESPUÉS DE SU VENCIMIENTO, EN CASO DE COBRO JUDICIAL PAGARÉ LOS GASTOS QUE SE ORIGINEN SOMETIÉNDOME A LOS TRIBUNALES QUE EL ACREEDOR ELIJA Y RENUNCIANDO AL FUERO DE MI ACTUAL O FUTURO DOMICILIO.';
    const pagareTexto = empresaConfig.formatoPagareTexto || pagareTextoDefault;
    const productos = venta.detalles || venta.productos || [];

    const productosHTML = productos.map(item => `
      <tr>
        <td style="padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${item.codigo_corto || item.codigo_barras || item.producto_id || ''}</td>
        <td style="padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${item.producto_nombre}</td>
        <td style="text-align:center;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${item.cantidad} ${abreviarUnidad(item.unidad_medida)}</td>
        <td style="text-align:right;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${formatMoney(item.precio_unitario)}</td>
        <td style="text-align:right;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${formatMoney(item.subtotal || item.cantidad * item.precio_unitario)}</td>
      </tr>`).join('');

    // Determinar copias a generar
    const copias = [];
    if (empresaConfig.formatoCopiaOriginal) copias.push('ORIGINAL');
    if (empresaConfig.formatoCopiaCliente) copias.push('COPIA CLIENTE');
    if (empresaConfig.formatoCopiaMostrador) copias.push('COPIA MOSTRADOR');
    if (copias.length === 0) copias.push('ORIGINAL');

    const marcaAguaHTML = empresaConfig.formatoMarcaAgua && logo ? `<div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);opacity:${opacidad};pointer-events:none;z-index:0;">
        <img src="${logo}" style="width:${tamanoMarcaAgua}px;height:${tamanoMarcaAgua}px;object-fit:contain;" /></div>` : '';

    const generarPagina = (etiqueta, esUltima) => `
      <div class="pagina-formato" style="position:relative;display:flex;flex-direction:column;min-height:100vh;page-break-inside:avoid;">
        <div style="flex:1;position:relative;z-index:1;">
          ${copias.length > 1 ? `<div style="text-align:right;font-size:8px;font-weight:700;color:${color};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">${etiqueta}</div>` : ''}
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:12px;border-bottom:2px solid ${color};">
            <div style="display:flex;align-items:center;gap:12px;">
              ${empresaConfig.formatoMostrarLogo && logo ? `<img src="${logo}" style="max-width:${logoTamano}px;max-height:${Math.round(logoTamano * 0.6)}px;object-fit:contain;" />` : ''}
              <div>
                ${empresaConfig.nombre ? `<div style="font-size:14px;font-weight:700;color:${color};">${empresaConfig.nombre}</div>` : ''}
                ${empresaConfig.formatoInfoEmpresa ? `<div style="font-size:${infoFontSize}px;color:#6b7280;max-width:250px;margin-top:2px;">${empresaConfig.formatoInfoEmpresa}</div>` : ''}
              </div>
            </div>
            <div style="display:flex;gap:10px;">
              <div style="border:2px solid ${color};border-radius:4px;min-width:120px;">
                <div style="background:${color};color:white;padding:3px 10px;font-size:9px;font-weight:600;text-transform:uppercase;">Vendedor</div>
                <div style="padding:5px 10px;font-size:11px;font-weight:500;">${venta.usuario_nombre || 'Sistema'}</div>
              </div>
              <div style="border:2px solid ${color};border-radius:4px;min-width:100px;">
                <div style="background:${color};color:white;padding:3px 10px;font-size:9px;font-weight:600;text-transform:uppercase;">Folio</div>
                <div style="padding:5px 10px;font-size:11px;font-weight:500;">#${venta.folio || venta.id}</div>
              </div>
            </div>
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:10px 14px;margin-bottom:14px;">
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px 16px;">
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Cliente</span><div style="font-size:${clienteFontSize}px;font-weight:600;">${venta.cliente_nombre || 'Cliente General'}</div></div>
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Teléfono</span><div style="font-size:${clienteFontSize}px;">${venta.cliente_telefono || ''}</div></div>
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Fecha</span><div style="font-size:${clienteFontSize}px;">${fecha}</div></div>
              ${venta.direccion_entrega ? `<div style="grid-column:span 2;"><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Domicilio</span><div style="font-size:${clienteFontSize}px;">${venta.direccion_entrega}</div></div>` : ''}
              <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Condiciones de pago</span><div style="font-size:${clienteFontSize}px;">${venta.metodo_pago === 'credito' ? 'Crédito' : venta.metodo_pago === 'tarjeta' ? 'Tarjeta' : 'Efectivo'}${venta.estatus_pago !== 'pagado' ? ' - Pendiente' : ''}</div></div>
              ${venta.observaciones ? `<div style="grid-column:span 3;"><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Observaciones</span><div style="font-size:${clienteFontSize}px;">${venta.observaciones}</div></div>` : ''}
            </div>
          </div>
          <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
            <thead><tr style="background:${color};">
              <th style="color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase;font-weight:600;width:70px;">Clave</th>
              <th style="color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase;font-weight:600;">Artículo</th>
              <th style="color:white;padding:5px 6px;text-align:center;font-size:9px;text-transform:uppercase;font-weight:600;width:60px;">Unidad</th>
              <th style="color:white;padding:5px 6px;text-align:right;font-size:9px;text-transform:uppercase;font-weight:600;width:70px;">Precio</th>
              <th style="color:white;padding:5px 6px;text-align:right;font-size:9px;text-transform:uppercase;font-weight:600;width:80px;">Importe</th>
            </tr></thead>
            <tbody>${productosHTML}</tbody>
          </table>
          <div style="text-align:center;font-size:10px;font-weight:600;color:${color};margin-bottom:12px;">
            ${venta.tipo_entrega === 'domicilio' ? 'ENTREGA A DOMICILIO' : 'RECOGER EN TIENDA'}
            ${venta.metodo_pago ? ` &bull; ${venta.metodo_pago === 'credito' ? 'CRÉDITO' : venta.metodo_pago.toUpperCase()}` : ''}
            ${venta.estatus_pago !== 'pagado' ? ' &bull; <span style="color:#dc2626;">PENDIENTE DE PAGO</span>' : ''}
          </div>
        </div>
        <div style="margin-top:auto;position:relative;z-index:1;">
          <div style="display:flex;justify-content:flex-end;margin-bottom:12px;">
            <div style="border:1.5px solid #374151;min-width:220px;">
              <div style="display:flex;border-bottom:1px solid #374151;">
                <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                  <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Subtotal</div>
                  <div style="font-size:10px;font-weight:500;text-align:right;">${empresaConfig.formatoDesgloseIva ? formatMoney(total / 1.16) : formatMoney(total)}</div>
                </div>
                <div style="flex:1;padding:4px 8px;">
                  <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">IVA ${empresaConfig.formatoDesgloseIva ? '16%' : 'al 0%'}</div>
                  <div style="font-size:10px;font-weight:500;text-align:right;">${empresaConfig.formatoDesgloseIva ? formatMoney(total - total / 1.16) : formatMoney(0)}</div>
                </div>
              </div>
              <div style="display:flex;background:${color};">
                <div style="flex:1;padding:6px 8px;">
                  <div style="font-size:7px;color:rgba(255,255,255,0.8);text-transform:uppercase;font-weight:600;">Total (MXN)</div>
                  <div style="font-size:14px;font-weight:700;color:white;text-align:right;">${formatMoney(total)}</div>
                </div>
              </div>
            </div>
          </div>
          ${mostrarPagare ? `
          <div style="border:1.5px solid #374151;margin:12px 0;page-break-inside:avoid;">
            <div style="display:flex;border-bottom:1px solid #374151;">
              <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Pagaré No.</div>
                <div style="font-size:9px;font-weight:700;">ÚNICO</div>
              </div>
              <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Emisión</div>
                <div style="font-size:9px;">${fecha}</div>
              </div>
              <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Importe</div>
                <div style="font-size:9px;font-weight:700;">${formatMoney(total)}</div>
              </div>
              <div style="flex:1;padding:4px 8px;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Folio</div>
                <div style="font-size:9px;font-weight:700;">#${venta.folio || venta.id}</div>
              </div>
            </div>
            <div style="padding:6px 8px;border-bottom:1px solid #374151;">
              <p style="font-size:7px;line-height:1.4;color:#374151;text-align:justify;">${pagareTexto
                .replace(/\{ciudad\}/g, venta.direccion_entrega || '')
                .replace(/\{fecha_emision\}/g, fecha)
                .replace(/\{empresa\}/g, empresaConfig.nombre)
                .replace(/\{importe\}/g, formatMoney(total))
                .replace(/\{importe_letra\}/g, '')
                .replace(/\{fecha_vencimiento\}/g, '')
              }</p>
            </div>
            <div style="display:flex;">
              <div style="flex:1;padding:6px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Otorgante</div>
                <div style="font-size:8px;">${empresaConfig.nombre}</div>
              </div>
              <div style="flex:1;padding:6px 8px;border-right:1px solid #374151;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Vencimiento</div>
                <div style="font-size:8px;"></div>
              </div>
              <div style="flex:1;padding:6px 8px;">
                <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Domicilio</div>
                <div style="font-size:8px;">${venta.direccion_entrega || ''}</div>
              </div>
            </div>
            <div style="display:flex;border-top:1px solid #374151;">
              <div style="flex:1;padding:8px;border-right:1px solid #374151;text-align:center;">
                <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                <div style="font-size:7px;color:#6b7280;">Nombre</div>
              </div>
              <div style="flex:1;padding:8px;border-right:1px solid #374151;text-align:center;">
                <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                <div style="font-size:7px;color:#6b7280;">Firma</div>
              </div>
              <div style="flex:1;padding:8px;text-align:center;">
                <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                <div style="font-size:7px;color:#6b7280;">Persona autorizada a recibir</div>
              </div>
            </div>
          </div>
          ` : ''}
          <div style="text-align:center;font-size:8px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
            ${empresaConfig.formatoPiePagina ? `<p style="margin-bottom:4px;">${empresaConfig.formatoPiePagina}</p>` : ''}
            <p>Impreso el ${new Date().toLocaleString('es-MX')} | Reimpresión</p>
          </div>
        </div>
      </div>
      ${!esUltima ? '<div style="page-break-after:always;"></div>' : ''}`;

    const paginasHTML = copias.map((etiqueta, idx) => generarPagina(etiqueta, idx === copias.length - 1)).join('');

    return `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <style>@page{size:letter;margin:0.8cm;}*{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;line-height:1.4;color:#1f2937;position:relative;padding:20px 24px;}
      @media print { .pagina-formato { min-height:100vh !important; } }
      </style></head><body>${marcaAguaHTML}${paginasHTML}</body></html>`;
  };

  // Reimprimir en formato carta
  const handleReimprimirFormato = async (venta) => {
    try {
      setLoading(true);
      // Si no tiene detalles, cargarlos
      let ventaCompleta = venta;
      if (!venta.detalles) {
        const result = await apiService.getVenta(venta.id);
        if (result.success) {
          ventaCompleta = result.data;
        } else {
          showToast('Error al cargar detalle de venta', 'error');
          return;
        }
      }

      const htmlContent = generarFormatoPedidoHTML(ventaCompleta);

      if (window.electron && window.electron.ipcRenderer) {
        // Obtener impresora para pedidos o la predeterminada
        let printerName = configImpresion.impresion_impresora_predeterminada || '';
        if (!printerName) {
          try {
            const printerResult = await window.electron.ipcRenderer.invoke('get-default-printer');
            if (printerResult.success && printerResult.printer) {
              printerName = printerResult.printer.name;
            }
          } catch (e) { /* fallback below */ }
        }
        if (!printerName) {
          printerName = 'Microsoft Print to PDF';
        }

        const result = await window.electron.ipcRenderer.invoke('print-document', {
          printerName,
          htmlContent,
          silent: false,
          options: { margins: { marginType: 'default' }, pageSize: 'Letter', landscape: false }
        });
        if (result.success) {
          showToast('Formato enviado a impresora', 'success');
        } else {
          showToast('Error al imprimir: ' + result.message, 'error');
        }
      } else {
        const ventana = window.open('', 'Imprimir Formato', 'width=800,height=600');
        if (ventana) {
          ventana.document.write(htmlContent);
          ventana.document.close();
          ventana.onload = () => { ventana.focus(); setTimeout(() => ventana.print(), 250); };
        }
      }
    } catch (error) {
      console.error('Error al reimprimir formato:', error);
      showToast('Error al reimprimir formato', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`sales-page ${pedidosPendientes.length > 0 ? 'has-pending-orders' : ''}`}>
      {/* Controles */}
      <div className="sales-controls">
        <div className="search-filters">
          <div className="search-container">
            <Input
              placeholder="Buscar por folio, cliente o monto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search size={20} />}
              className="search-input"
            />
          </div>

          {/* Filtro por tipo de entrega */}
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filtroTipoEntrega === 'todos' ? 'active' : ''}`}
              onClick={() => setFiltroTipoEntrega('todos')}
            >
              Todos
            </button>
            <button
              className={`filter-btn ${filtroTipoEntrega === 'domicilio' ? 'active' : ''}`}
              onClick={() => setFiltroTipoEntrega('domicilio')}
            >
              <Truck size={14} />
              Domicilio
            </button>
            <button
              className={`filter-btn ${filtroTipoEntrega === 'pickup' ? 'active' : ''}`}
              onClick={() => setFiltroTipoEntrega('pickup')}
            >
              <Package size={14} />
              Pickup
            </button>
          </div>

          {/* Filtro por fecha */}
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filtroFecha === 'hoy' ? 'active' : ''}`}
              onClick={() => setFiltroFecha(filtroFecha === 'hoy' ? '' : 'hoy')}
            >
              <Calendar size={14} />
              Hoy
            </button>
            <button
              className={`filter-btn ${filtroFecha === 'rango' ? 'active' : ''}`}
              onClick={() => setFiltroFecha(filtroFecha === 'rango' ? '' : 'rango')}
            >
              <Calendar size={14} />
              Rango
            </button>
          </div>

          {/* Inputs de rango de fechas */}
          {filtroFecha === 'rango' && (
            <div className="date-range-inputs">
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="date-input"
                placeholder="Fecha inicio"
              />
              <span style={{ color: '#6b7280' }}>hasta</span>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="date-input"
                placeholder="Fecha fin"
              />
            </div>
          )}

          {selectedVentas.length > 0 && (
            <div className="selection-info">
              <span className="selection-count">
                {selectedVentas.length} {selectedVentas.length === 1 ? 'venta seleccionada' : 'ventas seleccionadas'}
              </span>
              <button
                className="clear-selection-btn"
                onClick={() => setSelectedVentas([])}
                title="Limpiar selección"
              >
                Limpiar
              </button>
            </div>
          )}
        </div>

        <div className="controls-actions">
          {selectedVentas.length > 0 && (
            <Button
              variant="outline"
              onClick={handleImprimirPedidosSeleccionados}
              disabled={loading}
            >
              <Printer size={18} />
              Imprimir Pedidos ({selectedVentas.length})
            </Button>
          )}
          <Button onClick={() => setShowNewSaleModal(true)}>

            Nuevo Pedido
            <span className="keyboard-hint">F3</span>
          </Button>
        </div>
      </div>

      {/* Tabla de ventas */}
      <div className="sales-table-container">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Cargando ventas...</p>
          </div>
        )}

        {!loading && filteredVentas.length === 0 && (
          <div className="empty-state">
            <ShoppingCart size={48} />
            <h3>No se encontraron ventas</h3>
            <p>
              {searchQuery
                ? 'No hay ventas que coincidan con la búsqueda'
                : 'No hay ventas registradas'
              }
            </p>
          </div>
        )}

        {!loading && filteredVentas.length > 0 && (
          <table className="sales-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={areAllSelected()}
                    ref={input => {
                      if (input) {
                        input.indeterminate = areSomeSelected();
                      }
                    }}
                    onChange={handleSelectAll}
                    title={areAllSelected() ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  />
                </th>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Entrega</th>
                <th>Productos</th>
                <th>Total</th>
                <th>Estado</th>
                <th data-type="actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredVentas.map((venta) => (
                <tr key={venta.id} className={isVentaSelected(venta.id) ? 'selected-row' : ''}>
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={isVentaSelected(venta.id)}
                      onChange={() => handleSelectVenta(venta.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <div className="venta-folio">
                      <span className="folio-number">#{venta.folio || venta.id}</span>
                    </div>
                  </td>

                  <td>
                    <div className="venta-fecha">
                      <Calendar size={14} />
                      <span>{formatDate(venta.fecha_creacion)}</span>
                    </div>
                  </td>

                  <td>
                    <div className="venta-cliente">
                      <User size={14} />
                      <div className="cliente-info">
                        <span className="cliente-nombre">{venta.cliente_nombre || 'Cliente General'}</span>
                        {venta.cliente_nombre_comercial && (
                          <span className="cliente-nombre-comercial">{venta.cliente_nombre_comercial}</span>
                        )}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="venta-entrega">
                      {venta.tipo_entrega === 'domicilio' ? (
                        <>
                          <Truck size={14} />
                          <span>Domicilio</span>
                        </>
                      ) : (
                        <>
                          <Package size={14} />
                          <span>Pickup</span>
                        </>
                      )}
                    </div>
                  </td>

                  <td>
                    <div className="venta-productos">
                      <Package size={14} />
                      <span>{venta.total_productos || 0} productos</span>
                    </div>
                  </td>

                  <td style={{ textAlign: 'right' }}>
                    <div className="venta-total" style={{ justifyContent: 'flex-end' }}>
                      <span className="total-amount">{formatMoney(venta.total)}</span>
                    </div>
                  </td>

                  <td>
                    {venta.estado && (
                      <span className={`status-badge status-${venta.estado.toLowerCase()}`}>
                        {venta.estado}
                      </span>
                    )}
                  </td>

                  <td className="actions">
                    <div className="actions-compact">
                      <button
                        className="action-btn-compact"
                        onClick={() => handleViewDetail(venta)}
                        title="Ver detalle"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="action-btn-compact"
                        onClick={() => handleReimprimirVenta(venta)}
                        title="Reimprimir ticket"
                        disabled={loading}
                      >
                        <Printer size={14} />
                      </button>
                      <button
                        className="action-btn-compact"
                        onClick={() => handleReimprimirFormato(venta)}
                        title="Reimprimir formato carta"
                        disabled={loading}
                      >
                        <FileText size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de nueva venta */}
      {showNewSaleModal && (
        <NewSaleForm
          onClose={() => {
            setShowNewSaleModal(false);
            setPedidoActivo(null);
          }}
          onSaleCreated={(pedidoId) => {
            handleVentaCompletada(pedidoId || pedidoActivo?.id);
            setShowNewSaleModal(false);
          }}
          onPausar={handlePedidoPausado}
          pedidoInicial={pedidoActivo}
        />
      )}

      {/* Modal de detalle de venta */}
      {showDetailModal && selectedVenta && (
        <Modal
          title={`Detalle de Venta - ${selectedVenta.folio || `#${selectedVenta.id}`}`}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedVenta(null);
          }}
          size="large"
        >
          <div className="venta-detail-modal">
            {/* Información general */}
            <div className="detail-section">
              <h3>Información General</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Folio:</label>
                  <span>{selectedVenta.folio || `#${selectedVenta.id}`}</span>
                </div>
                <div className="detail-item">
                  <label>Fecha:</label>
                  <span>
                    <Calendar size={14} />
                    {formatDate(selectedVenta.fecha_creacion)}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Cliente:</label>
                  <span>
                    <User size={14} />
                    <div className="cliente-info-modal">
                      <span>{selectedVenta.cliente_nombre || 'Cliente General'}</span>
                      {selectedVenta.cliente_nombre_comercial && (
                        <span className="nombre-comercial-modal">{selectedVenta.cliente_nombre_comercial}</span>
                      )}
                    </div>
                  </span>
                </div>
                <div className="detail-item">
                  <label>Atendió:</label>
                  <span>{selectedVenta.usuario_nombre || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Tipo de Entrega:</label>
                  <span>
                    {selectedVenta.tipo_entrega === 'domicilio' ? (
                      <>
                        <Truck size={14} />
                        A Domicilio
                      </>
                    ) : (
                      <>
                        <Package size={14} />
                        Pasan por Él
                      </>
                    )}
                  </span>
                </div>
                {selectedVenta.tipo_entrega === 'domicilio' && selectedVenta.direccion_entrega && (
                  <div className="detail-item full-width">
                    <label>Dirección de Entrega:</label>
                    <span>
                      <MapPin size={14} />
                      {selectedVenta.direccion_entrega}
                    </span>
                  </div>
                )}
                {selectedVenta.estado && (
                  <div className="detail-item">
                    <label>Estado:</label>
                    <span className={`status-badge status-${selectedVenta.estado.toLowerCase()}`}>
                      {selectedVenta.estado}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Productos */}
            <div className="detail-section">
              <h3>Productos</h3>
              <table className="detail-products-table">
                <thead>
                  <tr>
                    <th>Cantidad</th>
                    <th>Producto</th>
                    <th>Precio Unitario</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedVenta.detalles && selectedVenta.detalles.length > 0 ? (
                    selectedVenta.detalles.map((detalle, index) => (
                      <tr key={index}>
                        <td>{detalle.cantidad}</td>
                        <td>{detalle.producto_nombre}</td>
                        <td>{formatMoney(detalle.precio_unitario)}</td>
                        <td className="text-right">{formatMoney(detalle.subtotal)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        No hay productos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totales */}
            <div className="detail-section">
              <div className="detail-totals">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>{formatMoney(selectedVenta.subtotal || 0)}</span>
                </div>
                {selectedVenta.descuento > 0 && (
                  <div className="total-row">
                    <span>Descuento:</span>
                    <span className="text-danger">-{formatMoney(selectedVenta.descuento)}</span>
                  </div>
                )}
                {selectedVenta.impuesto > 0 && (
                  <div className="total-row">
                    <span>Impuesto:</span>
                    <span>{formatMoney(selectedVenta.impuesto)}</span>
                  </div>
                )}
                <div className="total-row total-final">
                  <span>Total:</span>
                  <span>{formatMoney(selectedVenta.total || 0)}</span>
                </div>
              </div>
            </div>

            {/* Observaciones */}
            {selectedVenta.observaciones && (
              <div className="detail-section">
                <h3>Observaciones</h3>
                <p className="detail-observaciones">{selectedVenta.observaciones}</p>
              </div>
            )}

            {/* Acciones */}
            <div className="detail-actions">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedVenta(null);
                }}
              >
                Cerrar
              </Button>
              <Button
                variant="outline"
                onClick={() => handleReimprimirVenta(selectedVenta)}
              >
                <Printer size={18} />
                Reimprimir Ticket
              </Button>
              <Button
                onClick={() => handleReimprimirFormato(selectedVenta)}
              >
                <FileText size={18} />
                Reimprimir Formato
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de selección de ubicación de impresión */}
      {showPrintLocationModal && (
        <Modal
          title="Seleccionar ubicación de impresión"
          onClose={() => {
            setShowPrintLocationModal(false);
            setVentaParaImprimir(null);
          }}
          size="small"
        >
          <div className="print-location-modal">
            <p className="print-location-text">
              ¿Dónde desea imprimir el ticket?
            </p>
            <div className="print-location-buttons">
              <button
                className="print-location-btn local"
                onClick={() => handleSelectPrintLocation('local')}
              >
                <Monitor size={24} />
                <span>Local</span>
                <small>Impresora de este equipo</small>
              </button>
              <button
                className="print-location-btn almacen"
                onClick={() => handleSelectPrintLocation('almacen')}
              >
                <Warehouse size={24} />
                <span>Almacén</span>
                <small>Impresora del almacén</small>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Barra de pedidos pendientes */}
      <PendingOrdersBar
        pedidos={pedidosPendientes}
        onRetomar={handleRetomarPedido}
        onEliminar={handleEliminarPedidoPendiente}
      />
    </div>
  );
};

const SalesPage = () => {
  return (
    <GoogleMapsLoader>
      <SalesPageContent />
    </GoogleMapsLoader>
  );
};

export default SalesPage;